import { NavLink } from 'react-router';
import { PanelLeft, PanelLeftClose } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth';
import { navigationFor } from '@/lib/navigation';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { navIcon } from './nav-icon';
import { Logo, LogoMark } from './logo';

export function SidebarNav({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  /** Cierra el cajón en móvil al elegir un destino. */
  onNavigate?: () => void;
}) {
  const { user } = useAuth();
  const sections = navigationFor(user?.role);

  return (
    <nav aria-label="Navegación principal" className="flex flex-1 flex-col gap-4 overflow-y-auto px-2 py-3">
      {sections.map((section) => (
        <div key={section.title}>
          {!collapsed ? (
            <p className="px-2 pb-1.5 text-2xs font-semibold uppercase tracking-wide text-fg-subtle">
              {section.title}
            </p>
          ) : (
            // Sin el título visible el grupo debe seguir existiendo para el
            // lector de pantalla, o la lista se lee como un bloque plano.
            <p className="sr-only">{section.title}</p>
          )}
          <ul className="flex flex-col gap-0.5">
            {section.items.map((item) => {
              const Icon = navIcon(item.icon);
              const link = (
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium',
                      'transition-colors duration-fast ease-standard',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                      collapsed && 'justify-center px-0',
                      isActive
                        ? 'bg-primary-subtle text-primary-subtle-fg'
                        : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
                    )
                  }
                >
                  <Icon className="size-4 shrink-0" aria-hidden="true" />
                  <span className={cn('truncate', collapsed && 'sr-only')}>{item.label}</span>
                </NavLink>
              );

              return (
                <li key={item.to}>
                  {collapsed ? (
                    <Tooltip content={item.label} side="right">
                      {link}
                    </Tooltip>
                  ) : (
                    link
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
}: {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  return (
    <aside
      className={cn(
        // `surfaceSunken`, no `surface`: desde que el lienzo es blanco puro,
        // `surface` y `canvas` valen lo mismo y la barra lateral se fundía con
        // el contenido. El gris está reservado justo para esto — lo que debe
        // hundirse — y es lo único que separa el chrome del área de trabajo.
        'hidden shrink-0 flex-col border-r border-border bg-surface-sunken lg:flex',
        'transition-[width] duration-base ease-standard',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
    >
      <div className={cn('flex h-topbar shrink-0 items-center gap-2 border-b border-border px-3')}>
        {collapsed ? <LogoMark /> : <Logo className="h-8" />}
      </div>

      <SidebarNav collapsed={collapsed} />

      <div className="shrink-0 border-t border-border p-2">
        <Button
          variant="ghost"
          size={collapsed ? 'icon-sm' : 'sm'}
          onClick={onToggleCollapsed}
          className={cn('w-full', collapsed && 'w-auto')}
          aria-label={collapsed ? 'Expandir el menú' : 'Contraer el menú'}
          aria-expanded={!collapsed}
        >
          {collapsed ? <PanelLeft aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
          {!collapsed ? <span>Contraer</span> : null}
        </Button>
      </div>
    </aside>
  );
}
